import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllModerationEventsThunk,
  fetchGroupModerationEventsThunk,
  fetchPostModerationEventsThunk,
  createManualModerationEventThunk,
  analyzeAndFlagThunk,
  reanalyzePostThunk,
  updateModerationStatusThunk,
} from "./adminModerationActionThunks";

const initialState = {
  events: [],            // all events (paged; last fetch) -- regular posts, target: "post"
  groupEvents: [],        // group-post events (paged; last fetch) -- target: "group_post"
  postEvents: {},        // { [postId]: ModerationEventOut[] }
  loading: false,
  error: null,
  lastAction: null,      // "created" | "analyzed" | "reanalyzed" | "status-updated"
};

const moderationSlice = createSlice({
  name: "moderation",
  initialState,
  reducers: {
    resetModerationState: (state) => {
      state.loading = false;
      state.error = null;
      state.lastAction = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // List all
      .addCase(fetchAllModerationEventsThunk.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchAllModerationEventsThunk.fulfilled, (state, { payload }) => {
        state.events = payload || []; state.loading = false;
      })
      .addCase(fetchAllModerationEventsThunk.rejected, (state, { payload }) => {
        state.loading = false; state.error = payload;
      })

      // List all group-post events
      .addCase(fetchGroupModerationEventsThunk.fulfilled, (state, { payload }) => {
        state.groupEvents = payload || [];
      })
      .addCase(fetchGroupModerationEventsThunk.rejected, (state, { payload }) => {
        state.error = payload;
      })

      // List by post
      .addCase(fetchPostModerationEventsThunk.fulfilled, (state, { payload }) => {
        state.postEvents[payload.postId] = payload.items || [];
      })

      // Create manual
      .addCase(createManualModerationEventThunk.fulfilled, (state, { payload }) => {
        state.events.unshift(payload);
        state.lastAction = "created";
      })

      // Analyze & flag (may return null)
      .addCase(analyzeAndFlagThunk.fulfilled, (state, { payload }) => {
        if (payload) state.events.unshift(payload);
        state.lastAction = "analyzed";
      })

      // Reanalyze (may return null)
      .addCase(reanalyzePostThunk.fulfilled, (state, { payload }) => {
        if (payload) state.events.unshift(payload);
        state.lastAction = "reanalyzed";
      })

      // Update status -- payload is either a ModerationEventOut (target: "post")
      // or a GroupModerationEventOut (target: "group_post"); route the update
      // into whichever array actually holds that row, since they're two
      // independent id sequences and an id match in the wrong array would be
      // a coincidence, not the right row.
      .addCase(updateModerationStatusThunk.fulfilled, (state, { payload }) => {
        if (payload?.target === 'group_post') {
          const idx = state.groupEvents.findIndex(e => e.id === payload.id);
          if (idx >= 0) state.groupEvents[idx] = payload;
        } else {
          const idx = state.events.findIndex(e => e.id === payload.id);
          if (idx >= 0) state.events[idx] = payload;
        }
        state.lastAction = "status-updated";
      })

      // Common rejections
      .addCase(createManualModerationEventThunk.rejected, (s, a) => { s.error = a.payload; })
      .addCase(analyzeAndFlagThunk.rejected, (s, a) => { s.error = a.payload; })
      .addCase(reanalyzePostThunk.rejected, (s, a) => { s.error = a.payload; })
      .addCase(updateModerationStatusThunk.rejected, (s, a) => { s.error = a.payload; });
  },
});

export const { resetModerationState } = moderationSlice.actions;
export default moderationSlice.reducer;

// Selectors
export const selectAllModEvents = (state) => state.moderation.events;
export const selectAllGroupModEvents = (state) => state.moderation.groupEvents;
export const selectPostModEvents = (state, postId) => state.moderation.postEvents[postId] || [];
export const selectModLoading = (state) => state.moderation.loading;
export const selectModError = (state) => state.moderation.error;
